"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useVisibility } from "src/lib/Visibility";

export interface LoadWhenVisibleOptions {
  enabled?: boolean;
  rootMargin?: string;
  threshold?: number;
}

export const useLoadWhenVisible = <TElement extends Element>(
  options: LoadWhenVisibleOptions = {},
) => {
  const { enabled = true, rootMargin = "0px", threshold = 0 } = options;
  const { visible: contextVisible } = useVisibility();
  const [element, setElement] = useState<TElement | null>(null);
  const [intersecting, setIntersecting] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setIntersecting(true);
      return;
    }
    if (!contextVisible || !element) {
      setIntersecting(false);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry?.isIntersecting ?? false),
      { rootMargin, threshold },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [contextVisible, element, enabled, rootMargin, threshold]);

  return {
    loadRef: setElement as React.RefCallback<TElement>,
    shouldLoad: !enabled || (contextVisible && intersecting),
  };
};
